// backendServices/liveStreamManagment/save_file_picker.ts
//
// Choix de la destination d'un export (deck / mediaboard).
//
// `showSaveFilePicker` (File System Access API) n'existe que sur Chrome/Edge, en
// contexte sécurisé. Ailleurs on retombe sur le téléchargement classique
// (<a download>) : le fichier part dans le dossier de téléchargement du navigateur.
//
// ⚠️ Le sélecteur exige un geste récent de l'utilisateur (quelques secondes). Il doit
// donc être ouvert AU CLIC, avant tout `await` long (envoi des fichiers, zip…), puis
// le fichier est écrit dans le handle obtenu une fois le blob prêt.

export type ExportExt = 'zip' | 'json';

export interface SaveTarget {
  handle: any;       // FileSystemFileHandle
  ext   : ExportExt;
}

/** Ce que renvoie `pickSaveTarget` : une cible, `null` (non supporté → repli), ou `'cancelled'`. */
export type PickResult = SaveTarget | null | 'cancelled';

const MIME: Record<ExportExt, string> = { zip: 'application/zip', json: 'application/json' };

function cleanName(name: string, ext: ExportExt): string {
  return name.replace(/[^a-z0-9_\-. ]/gi, '_') + '.' + ext;
}

export async function pickSaveTarget(baseName: string, ext: ExportExt): Promise<PickResult> {
  const picker = (window as any).showSaveFilePicker;
  if (typeof picker !== 'function') return null;
  try {
    const handle = await picker.call(window, {
      suggestedName: cleanName(baseName, ext),
      types: [{ description: ext === 'zip' ? 'Archive ZIP' : 'Fichier JSON', accept: { [MIME[ext]]: ['.' + ext] } }],
    });
    return { handle, ext };
  } catch (e: any) {
    if (e?.name === 'AbortError') return 'cancelled';
    // Bloqué (iframe, politique…) : on ne perd pas l'export, on télécharge normalement.
    console.warn('[save_file_picker] sélecteur indisponible, repli sur le téléchargement :', e);
    return null;
  }
}

function downloadViaLink(blob: Blob, filename: string, ext: ExportExt): void {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = cleanName(filename, ext);
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Écrit le blob dans la cible choisie. Sans cible, ou si l'extension réellement produite
 * n'est pas celle annoncée au sélecteur, ou si l'écriture échoue : téléchargement classique.
 */
export async function saveBlob(blob: Blob, filename: string, ext: ExportExt, target?: SaveTarget | null): Promise<void> {
  if (target && target.ext === ext) {
    try {
      const w = await target.handle.createWritable();
      await w.write(blob);
      await w.close();
      return;
    } catch (e) {
      console.warn('[save_file_picker] écriture impossible, repli sur le téléchargement :', e);
    }
  }
  downloadViaLink(blob, filename, ext);
}
